import { Fragment, useCallback, useState } from 'react';
import refresh from '~/assets/icons/refresh.svg';
import user from '~/assets/icons/user_2.svg';
import { CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { SectionHeading } from '~/components/ui/SectionHeading';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import {
	IExchangeItemHouse,
	IExchangeItemMoney,
	IExchangeItemNumberPlate,
	IExchangeItemVehicle,
	rage,
	useObservable,
} from '~/rage';
import { Currency, ExchangeItemType } from '~/shared';
import { Divider } from '../Divider';
import { ListItem } from '../ListItem';
import { MoneyInput } from '../MoneyInput';
import {
	StyledBottomContainer,
	StyledCashInputContainer,
	StyledContainer,
	StyledHead,
	StyledNotActiveContainer,
	StyledOfferList,
	StyledTaxContainer,
} from './Offer.styled';

const Offer: React.FC = () => {
	const otherPlayerName = useObservable(rage.exchange.getOtherPlayerName());
	const ownedItems = useObservable(rage.exchange.getOwnedItems());
	const [scrollOffsetPlayer1, setScrollOffsetPlayer1] = useState<number>(0);
	const [scrollOffsetPlayer2, setScrollOffsetPlayer2] = useState<number>(0);
	const localPlayerPlacedItems = useObservable(
		rage.exchange.getLocalPlayerPlacedItems(),
	);
	const otherPlayerPlacedItems = useObservable(
		rage.exchange.getOtherPlayerPlacedItems(),
	);
	const exchangeTax = useObservable(rage.exchange.getExchangeTax());
	const isLocalPlayerConfirmedItems = useObservable(
		rage.exchange.isLocalPlayerConfirmedItems(),
	);
	const [moneyInputValue, setMoneyInputValue] = useState<number>(0);

	const rubleIcon = useWalletIcon(Currency.Ruble, true);

	const handleScrollPlayer1 = useCallback((e: React.UIEvent<HTMLElement>) => {
		const scrollTop = (e as any).target?.scrollTop;
		if (scrollTop) {
			setScrollOffsetPlayer1(scrollTop);
		}
	}, []);

	const handleScrollPlayer2 = useCallback((e: React.UIEvent<HTMLElement>) => {
		const scrollTop = (e as any).target?.scrollTop;
		if (scrollTop) {
			setScrollOffsetPlayer2(scrollTop);
		}
	}, []);

	const handleAddMoney = useCallback(async () => {
		if (moneyInputValue === 0) {
			return;
		}

		const moneyId = ownedItems.find(
			(item) => item.type === ExchangeItemType.Money,
		)?.id;

		if (!moneyId) {
			return;
		}

		await rage.exchange.placeItem(moneyId, moneyInputValue);
	}, [moneyInputValue, ownedItems]);

	return (
		<StyledContainer>
			<StyledHead>
				<SectionHeading icon={refresh} text={'Обмен'} />
				<Text>(Мое предложение)</Text>
			</StyledHead>
			<Divider style={{ marginTop: 20, width: 'calc(100% - 20px)' }} />
			<StyledOfferList style={{ height: 287 }} onScroll={handleScrollPlayer1}>
				{localPlayerPlacedItems.length ? (
					localPlayerPlacedItems.map((item, index) => {
						if (rage.exchange.isItemVehicle(item)) {
							return (
								<Fragment key={index}>
									<ListItem
										id={item.id}
										type={item.type}
										mode={'remove'}
										text={(item as IExchangeItemVehicle).vehicleName}
										number={(item as IExchangeItemVehicle).numberPlate}
										scrollOffset={scrollOffsetPlayer1}
									/>
									<Divider />
								</Fragment>
							);
						}
						if (rage.exchange.isItemHouse(item)) {
							return (
								<Fragment key={index}>
									<ListItem
										id={item.id}
										type={item.type}
										mode={'remove'}
										text={`Дом №${(item as IExchangeItemHouse).houseId}`}
										scrollOffset={scrollOffsetPlayer1}
									/>
									<Divider />
								</Fragment>
							);
						}
						if (rage.exchange.isItemNumberPlate(item)) {
							return (
								<Fragment key={index}>
									<ListItem
										id={item.id}
										type={item.type}
										mode={'remove'}
										text={''}
										number={(item as IExchangeItemNumberPlate).numberPlate}
										scrollOffset={scrollOffsetPlayer1}
									/>
									<Divider />
								</Fragment>
							);
						}
						return (
							<Fragment key={index}>
								<ListItem
									id={item.id}
									type={item.type}
									mode={'remove'}
									text={(item as IExchangeItemMoney).amount.toLocaleString(
										'ru-RU',
									)}
									scrollOffset={scrollOffsetPlayer1}
								/>
								<Divider />
							</Fragment>
						);
					})
				) : (
					<StyledNotActiveContainer>
						<Text>Вы не добавили имущества</Text>
					</StyledNotActiveContainer>
				)}
			</StyledOfferList>
			<Divider style={{ width: 'calc(100% - 20px)' }} />
			<StyledCashInputContainer>
				<MoneyInput
					value={moneyInputValue}
					onChange={setMoneyInputValue}
					placeholder={'Введите свою доплату'}
				/>
				<Button
					color={ButtonColors.Select2}
					height={ButtonHeight.Height40}
					onClick={handleAddMoney}
					style={{ width: 110 }}
				>
					<Text>Добавить</Text>
				</Button>
			</StyledCashInputContainer>
			<StyledHead style={{ marginTop: 30 }}>
				<SectionHeading icon={user} text={otherPlayerName || ''} />
				<Text>(Предложение игрока)</Text>
			</StyledHead>
			<Divider style={{ marginTop: 20, width: 'calc(100% - 20px)' }} />
			<StyledOfferList style={{ height: 277 }} onScroll={handleScrollPlayer2}>
				{otherPlayerPlacedItems.length ? (
					otherPlayerPlacedItems.map((item, index) => {
						if (rage.exchange.isItemVehicle(item)) {
							return (
								<Fragment key={index}>
									<ListItem
										id={item.id}
										type={item.type}
										mode={'remove'}
										text={(item as IExchangeItemVehicle).vehicleName}
										number={(item as IExchangeItemVehicle).numberPlate}
										scrollOffset={scrollOffsetPlayer2}
										owned={false}
									/>
									<Divider />
								</Fragment>
							);
						}
						if (rage.exchange.isItemHouse(item)) {
							return (
								<Fragment key={index}>
									<ListItem
										id={item.id}
										type={item.type}
										mode={'remove'}
										text={`Дом №${(item as IExchangeItemHouse).houseId}`}
										scrollOffset={scrollOffsetPlayer2}
										owned={false}
									/>
									<Divider />
								</Fragment>
							);
						}
						if (rage.exchange.isItemNumberPlate(item)) {
							return (
								<Fragment key={index}>
									<ListItem
										id={item.id}
										type={item.type}
										mode={'remove'}
										text={''}
										number={(item as IExchangeItemNumberPlate).numberPlate}
										scrollOffset={scrollOffsetPlayer2}
										owned={false}
									/>
									<Divider />
								</Fragment>
							);
						}
						return (
							<Fragment key={index}>
								<ListItem
									id={item.id}
									type={item.type}
									mode={'remove'}
									text={(item as IExchangeItemMoney).amount.toLocaleString(
										'ru-RU',
									)}
									scrollOffset={scrollOffsetPlayer2}
									owned={false}
								/>
								<Divider />
							</Fragment>
						);
					})
				) : (
					<StyledNotActiveContainer>
						<Text>Другой игрок не добавил имущество</Text>
					</StyledNotActiveContainer>
				)}
			</StyledOfferList>
			<Divider style={{ width: 'calc(100% - 20px)' }} />
			<StyledBottomContainer>
				<StyledTaxContainer>
					<Text>Налог на сделку:</Text>
					<CurrencyIcon icon={rubleIcon} size="sm" />
					<Text>{exchangeTax.toLocaleString('ru-RU')}</Text>
				</StyledTaxContainer>
				<Text
					style={{
						fontSize: 16,
						color: '#ffffff',
						margin: '0 auto 0 20px',
						opacity: 0.25,
					}}
				>
					Налог взымается с игрока
				</Text>
				<Button
					color={ButtonColors.Buy}
					height={ButtonHeight.Height40}
					disabled={isLocalPlayerConfirmedItems}
					onClick={() => rage.exchange.confirmItems()}
					style={{ width: 184 }}
				>
					<Text>
						{isLocalPlayerConfirmedItems
							? 'Ожидание игрока'
							: 'Подтвердить обмен'}
					</Text>
				</Button>
			</StyledBottomContainer>
		</StyledContainer>
	);
};

export { Offer };
